import { useState, createContext, useContext, useCallback } from 'react'
import type { ReactNode } from 'react'

export type MessageType = 'message' | 'cta' | 'error'

export const DISMISSABLE_MESSAGES: MessageType[] = ['message', 'error']

interface MessageAction {
  label: string
  to?: string
  onClick?: () => void
}

export interface Message {
  id: string
  type: MessageType
  content: string | ReactNode
  dismissable?: boolean
  action?: MessageAction
  duration?: number
}

type MessageState = {
  messages: Message[]
  addMessage: (message: Omit<Message, 'id'>) => string
  removeMessage: (id?: string) => void
  clearErrors: () => void
}

let messageCounter = 0

export const SetupMessageStore = (): MessageState => {
  const [messages, setMessages] = useState<Message[]>([])

  const removeMessage = useCallback((id?: string) => {
    if (id) {
      setMessages((prev) => prev.filter((m) => m.id !== id))

      return
    }

    setMessages((prev) =>
      prev.filter((m) => !DISMISSABLE_MESSAGES.includes(m.type))
    )
  }, [])

  const clearErrors = useCallback(() => {
    setMessages((prev) => prev.filter((m) => m.type !== 'error'))
  }, [])

  const addMessage = useCallback((msg: Omit<Message, 'id'>): string => {
    const id = `msg-${++messageCounter}`
    const message: Message = { ...msg, id }

    setMessages((prev) => {
      const withoutDuplicate = prev.filter((m) => m.content !== msg.content)
      return [...withoutDuplicate, message]
    })

    return id
  }, [])

  return { messages, addMessage, removeMessage, clearErrors }
}

const initialState: MessageState = {
  messages: [],
  addMessage: () => '',
  removeMessage: () => null,
  clearErrors: () => null
}

export const MessageStoreContext = createContext<MessageState>(initialState)

export const useMessageStore = () => {
  return useContext(MessageStoreContext)
}
